import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import { Alert, Button, Input, Modal, Select, Skeleton, useToast } from '@/components/ui';
import { formatMoney } from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';

/**
 * Put a company on a plan from the company's own record.
 *
 * The subscriptions roster can only reach companies that already have a
 * subscription row, and a company on the free plan has none — which is most of
 * them, and exactly the company somebody has just sold to. So assigning is
 * offered here, where an admin is already looking at the customer.
 *
 * It goes through the same service the company's own screen calls, so an
 * upgrade is billed now and a downgrade waits for the period already paid for.
 * Nothing about the billing rules changes because staff did it.
 */
export function AssignPlanAction({ companyId, companyName, hasSubscription }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [planCode, setPlanCode] = useState('');
  const [couponCode, setCouponCode] = useState('');

  const plans = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminApi.listBillingPlans,
    enabled: open,
  });

  const assign = useMutation({
    mutationFn: () =>
      adminApi.assignSubscription({
        companyId,
        planCode,
        couponCode: couponCode.trim() || undefined,
      }),
    onSuccess: (result) => {
      toast.success(
        result.effective_immediately
          ? `${companyName} is on ${planCode} from now`
          : `${companyName} moves to ${planCode} at the end of the paid period`,
      );
      setOpen(false);
      setCouponCode('');
      // Every admin screen shows a plan somewhere.
      void queryClient.invalidateQueries({
        predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
      });
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not assign the plan')),
  });

  // Only paid plans are worth assigning: moving a company to free is a
  // cancellation, and that belongs on the subscription record with the rest of
  // the lifecycle.
  // Student plans are a different catalogue and the server refuses them here.
  const sellable = (plans.data ?? []).filter(
    (plan) =>
      plan.audience !== 'STUDENT' && plan.is_active && !plan.is_free_plan && plan.price_cents > 0,
  );

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {hasSubscription ? 'Change plan' : 'Assign a plan'}
      </Button>

      <Modal
        open={open}
        title={`Put ${companyName} on a plan`}
        description="Billed by the ordinary rules: a bigger plan starts and invoices now, a smaller one waits for the period already paid for."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={assign.isPending}>
              Cancel
            </Button>
            <Button
              disabled={!planCode}
              isLoading={assign.isPending}
              onClick={() => assign.mutate()}
            >
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {plans.isPending ? (
            <div className="space-y-2" role="status" aria-label="Loading plans">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10" />
            </div>
          ) : plans.isError ? (
            <Alert tone="error" title="Could not load the plans">
              <p>{errorMessage(plans.error)}</p>
              <Button
                variant="secondary"
                size="xs"
                className="mt-2"
                onClick={() => void plans.refetch()}
              >
                Try again
              </Button>
            </Alert>
          ) : sellable.length === 0 ? (
            <Alert tone="warning">
              There are no paid plans to assign. Create one under Plans first.
            </Alert>
          ) : (
            <Select
              label="Plan"
              value={planCode}
              onChange={(event) => setPlanCode(event.target.value)}
            >
              <option value="">Choose a plan</option>
              {sellable.map((plan) => (
                <option key={plan.code} value={plan.code}>
                  {plan.name} — {formatMoney(plan.price_cents, plan.currency)}
                </option>
              ))}
            </Select>
          )}

          <Input
            label="Discount code"
            hint="Optional. Applied to the invoice this produces, and spent on the company's behalf."
            value={couponCode}
            onChange={(event) => setCouponCode(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
